import { UploadPasscodeListMemberResponse, WorkingPasscodeList } from '@/types/passcode-list.types';
import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import Modal from '@ui/modal/modal';
import Text from '@ui/text/text/text';
import { Dispatch, useCallback, useEffect, useState } from 'react';
import AddPasscodeListMembers from './add-passcode-list-members';
import { allListMembersValid, getDefaultPasscodeList, mapToWorkingPasscodeList } from '../../utils';
import { PASSCODE_LIST_TYPE, PasscodeList } from '@/types/registration.types';
import OptionalComponent from '@ui/optional-component/optional-component';
import {
  GenerateMagicLinksForEvent,
  GenerateMagicLinksForList,
  SendTestMagicLinkRegistrationEmail,
  SendUnsentMagicLinkRegistrationEmails,
  UploadMagicLinkPasscodeListMembers,
  UploadPasscodeListMembers,
} from '@/connection/audiences/passcode-lists';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { getActiveChannel } from '@/store/slices/user/selectors';
import toastTrigger from '@ui/toast/toast-trigger';
import { getFetchErrorMessage } from '@/connection/helpers';
import {
  getPublishedStatus,
  getWorkingProjectUuid,
  selectWorkingProjectMagicLinkRegistrationsPasscodeListId,
  selectProjectName,
  selectWorkingProjectRegistrationSettings,
} from '@/store/slices/project/selectors';
import { selectPasscodeLists } from '@/store/slices/passcode-lists/selectors';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import ConfirmUploadRegistrantsModal from '../registrants/modals/confirm-upload-registrants-modal';
import { EProjectStatus } from '@/types/projects.types';
import useGetProjectPublishedStatus from '@/connection/projects/use-get-project-published-status';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import { EToastTypes } from '@ui/toast/toast.types';
import getThemeColorsCssMixVariable from '@/utils/colors/get-theme-color-css-mix-variable';

type AddPasscodelistMembersModalProps = {
  open: boolean;
  setOpen: Dispatch<React.SetStateAction<boolean>>;
  passcodeList: PasscodeList | null;
  isMagicLinkRegistrations?: boolean;
  reloadMembers?: (newPasscodeListId: number) => void;
};

const AddPasscodeListMembersModal = ({
  open,
  setOpen,
  passcodeList,
  isMagicLinkRegistrations,
  reloadMembers,
}: AddPasscodelistMembersModalProps) => {
  const dispatch = useAppDispatch();
  const channel = useAppSelector(getActiveChannel);
  const projectName = useAppSelector(selectProjectName);
  const projectUuid = useAppSelector(getWorkingProjectUuid);
  const magicLinkRegistrationsPasscodeListId = useAppSelector(selectWorkingProjectMagicLinkRegistrationsPasscodeListId);
  const passcodeLists = useAppSelector(selectPasscodeLists);
  const registrationSettings = useAppSelector(selectWorkingProjectRegistrationSettings);
  const publishedStatus = useAppSelector(getPublishedStatus);
  const isPublished = publishedStatus?.status === EProjectStatus.PUBLIC;

  useGetProjectPublishedStatus();

  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [workingPasscodeList, setWorkingPasscodeList] = useState<WorkingPasscodeList>(
    mapToWorkingPasscodeList(projectUuid, passcodeList, isMagicLinkRegistrations)
  );
  const [fileName, setFileName] = useState<string | null>(null);
  const [sendTestEmailModalOpen, setSendTestEmailModalOpen] = useState(false);
  const [testEmailAddressesForSend, setTestEmailAddressesForSend] = useState<string[]>([]);

  const closeAndResetState = (newPasscodeListId: number) => {
    setFileName(null);
    setSaving(false);
    setSendTestEmailModalOpen(false);
    setTestEmailAddressesForSend([]);
    setOpen(false);

    if (reloadMembers) {
      reloadMembers(newPasscodeListId);
    }
  };

  const handleSaving = async (isTestEmailSend = false) => {
    // Preview-only test send: hit a dedicated endpoint that just emails the addresses and skip the
    // passcode/list upload + generate + send-unsent machinery entirely.
    if (isTestEmailSend) {
      if (!projectUuid || !testEmailAddressesForSend.length) return;

      setSendingTest(true);
      try {
        const response = await SendTestMagicLinkRegistrationEmail(projectUuid, testEmailAddressesForSend);
        if (response?.success) {
          toastTrigger('Test email sent', {
            description: 'A preview registration email was sent to the test address(es).',
            type: EToastTypes.success,
            autoClose: 10000,
          });
        } else {
          toastTrigger('There was a problem sending the test email', {
            type: EToastTypes.error,
            autoClose: 10000,
          });
        }
      } catch (e) {
        displayCaughtError(e, 'There was a problem sending the test email.');
      } finally {
        setSendingTest(false);
      }
      return;
    }

    if (
      !channel ||
      !projectUuid ||
      (workingPasscodeList?.type !== PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION && !passcodeList?.passcode_list) ||
      (!workingPasscodeList?.type && workingPasscodeList?.type !== PASSCODE_LIST_TYPE.EMAILS_ONLY)
    ) {
      return; //EMAILS_ONLY is falsy
    }

    // The test-email send and the real save share this handler. Route the loading state to a
    // dedicated flag for the test so triggering "Send Test" doesn't spin the real "Send"/"Save" button.
    const setBusy = isTestEmailSend ? setSendingTest : setSaving;

    setBusy(true);
    //magic links require a published project, so verify the status
    if (
      [PASSCODE_LIST_TYPE.MAGIC_LINKS, PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION].includes(
        workingPasscodeList.type
      ) &&
      !isPublished
    ) {
      toastTrigger('Please Publish Project First', {
        description: `You need to publish ${projectName} first before you can attach a magic links list.`,
        type: EToastTypes.error,
        autoClose: 10000,
      });

      setBusy(false);
      return;
    }

    const listToCreate = {
      ...workingPasscodeList,
      name: workingPasscodeList.name.trim(),
    };

    if (isTestEmailSend) {
      //If we are dealing with a test email, we want to complete the process for only the emails added in the test email modal.
      listToCreate.url = '';
      listToCreate.manually_added_users = testEmailAddressesForSend.map((email) => ({
        email,
        first_name: 'Test',
        last_name: 'Email',
        passcode: '',
      }));
    }

    // Duplicate-name validation only matters when creating/saving the real list. Skip it for the
    // test-email send, which just emails the entered addresses and shouldn't be blocked by it.
    if (!isTestEmailSend) {
      const lowerCaseName = listToCreate.name.toLowerCase();
      const isDuplicate = passcodeLists.some((list) => {
        return lowerCaseName === list.name?.toLowerCase() && !magicLinkRegistrationsPasscodeListId;
      });
      if (isDuplicate) {
        toastTrigger('Duplicate Audience List Name', {
          description: `"${listToCreate.name}" is already in use. Please enter a unique name.`,
          type: EToastTypes.error,
          autoClose: 10000,
        });
        setBusy(false);
        return;
      }
    }

    let message = '';
    const addMembersResponse: UploadPasscodeListMemberResponse | undefined = await tryUploadRequest(
      isTestEmailSend,
      listToCreate,
      channel,
      projectUuid
    );

    if (
      !addMembersResponse ||
      ([PASSCODE_LIST_TYPE.MAGIC_LINKS, PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION].includes(
        workingPasscodeList?.type
      ) &&
        (!addMembersResponse || !addMembersResponse.passcode_list || !addMembersResponse.success))
    ) {
      toastTrigger('There was a problem saving the list', {
        type: EToastTypes.error,
        autoClose: 10000,
      });
      setBusy(false);
      return;
    }

    message = addMembersResponse.message || 'List saved successfully.';

    // ATTACH EVENT
    if (
      addMembersResponse.type &&
      [
        PASSCODE_LIST_TYPE.EMAILS_ONLY,
        PASSCODE_LIST_TYPE.EMAILS_AND_PASSCODES,
        PASSCODE_LIST_TYPE.PASSCODES_ONLY,
      ].includes(addMembersResponse.type)
    ) {
      toastTrigger(message, {
        type: EToastTypes.success,
        autoClose: 10000,
      });
      if (passcodeList?.passcode_list) {
        closeAndResetState(passcodeList.passcode_list);
      }

      return;
    }

    if (
      addMembersResponse.type === PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION &&
      !magicLinkRegistrationsPasscodeListId &&
      addMembersResponse.passcode_list &&
      addMembersResponse?.passcode_list > 0
    ) {
      //if we just created a magic link registrations list, store the ref to the passcode_list in registration settings since it is not a true gate
      if (registrationSettings) {
        const _updatedSettings = {
          ...registrationSettings,
          magic_links_registration_passcode_list: addMembersResponse.passcode_list,
        };
        dispatch(saveWorkingProjectByFields({ registration_settings: _updatedSettings }));
      }
    }

    //create the magic links for types that need it
    if (
      [PASSCODE_LIST_TYPE.MAGIC_LINKS, PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION].includes(
        workingPasscodeList.type
      ) &&
      addMembersResponse.passcode_list &&
      addMembersResponse.type
    ) {
      try {
        if (workingPasscodeList.type === PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION && isMagicLinkRegistrations) {
          await GenerateMagicLinksForEvent(
            channel,
            addMembersResponse.passcode_list,
            projectUuid,
            addMembersResponse.type
          );
        } else {
          await GenerateMagicLinksForList(channel, addMembersResponse.passcode_list);
        }
      } catch (e) {
        displayCaughtError(e, 'There was a problem generating magic links for the list.');
        setBusy(false);
        return;
      }
    }

    let sendEmailsError = '';
    //magic link registration, so needs to send out emails
    if (
      workingPasscodeList.type === PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION &&
      projectUuid &&
      addMembersResponse.passcode_list
    ) {
      try {
        //send out the registration emails for the magic link registrations
        const response = await SendUnsentMagicLinkRegistrationEmails(projectUuid, addMembersResponse.passcode_list);
        if (response.success) {
          // technically speaking "Registration emails sent successfully." is not true,
          // The send endpoint returns success as soon as the send job is triggered - it does not
          // report how many emails actually went out - so we're claiming delivery here when we're not 100% sure.
          // It should probably be something like "Registration invites are being sent." but that's a product call.
          message += ` ${response.message || 'Registration emails sent successfully.'}`;
        } else {
          //if the send failed or partially failed, we want to display an error message in addition to the add to list success message
          sendEmailsError +=
            'We were unable to send some or all magic link emails. Please re-trigger the email by clicking "send unsent invites" button.';
        }
      } catch (e) {
        sendEmailsError += getFetchErrorMessage(e, 'There was a problem sending the magic link registration emails.');
        console.error(e);
      }
    }

    if (message.length) {
      //list is all good, so say so. Send email
      toastTrigger(message, {
        type: EToastTypes.success,
        autoClose: 10000,
      });
    }

    if (sendEmailsError.length) {
      toastTrigger(sendEmailsError, {
        type: EToastTypes.error,
        autoClose: 10000,
      });
    }

    //only close and reset the modal state if we are not sending test emails.
    //if we are sending test emails, we want to keep the modal open so they can submit the live data
    if (!isTestEmailSend) {
      if (addMembersResponse.passcode_list) {
        closeAndResetState(addMembersResponse.passcode_list);
      } else if (passcodeList && passcodeList.passcode_list) {
        closeAndResetState(passcodeList.passcode_list);
      }
    } else {
      setBusy(false);
    }
  };

  const tryUploadRequest = async (
    isTestEmailSend: boolean,
    listToCreate: WorkingPasscodeList,
    channel: number,
    projectUuid: string
  ) => {
    if (
      channel &&
      (passcodeList?.passcode_list ||
        (!passcodeList?.passcode_list && workingPasscodeList.type === PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION)) &&
      (workingPasscodeList?.type || workingPasscodeList?.type === PASSCODE_LIST_TYPE.EMAILS_ONLY)
    ) {
      //EMAILS_ONLY is falsy
      try {
        if (
          isTestEmailSend &&
          workingPasscodeList.type === PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION &&
          isMagicLinkRegistrations
        ) {
          // A test reuses the event's existing registration list. If the ref in registration_settings
          // is missing, resolve it by name so we append to that list instead of trying to create a
          // duplicate (which the API rejects) - otherwise the test would never send.
          const registrationListId =
            magicLinkRegistrationsPasscodeListId ||
            passcodeLists.find((list) => list.name?.toLowerCase() === listToCreate?.name?.toLowerCase())?.passcode_list;
          return await UploadMagicLinkPasscodeListMembers(
            channel,
            listToCreate?.name,
            registrationListId,
            listToCreate?.type,
            listToCreate.url,
            listToCreate.manually_added_users,
            projectUuid
          );
        } else if (
          !isTestEmailSend &&
          workingPasscodeList.type === PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION &&
          isMagicLinkRegistrations
        ) {
          return await UploadMagicLinkPasscodeListMembers(
            channel,
            workingPasscodeList?.name,
            magicLinkRegistrationsPasscodeListId,
            workingPasscodeList?.type,
            workingPasscodeList.url,
            workingPasscodeList.manually_added_users,
            projectUuid
          );
        } else if (workingPasscodeList.type === PASSCODE_LIST_TYPE.MAGIC_LINKS) {
          return await UploadMagicLinkPasscodeListMembers(
            channel,
            workingPasscodeList?.name,
            passcodeList?.passcode_list,
            workingPasscodeList?.type,
            workingPasscodeList.url,
            workingPasscodeList.manually_added_users,
            projectUuid
          );
        } else if (passcodeList?.passcode_list) {
          return await UploadPasscodeListMembers(
            channel,
            fileName || '',
            passcodeList?.passcode_list,
            passcodeList?.type,
            workingPasscodeList.url,
            workingPasscodeList.manually_added_users
          );
        }
      } catch (e) {
        displayCaughtError(e, 'There was a problem saving the list.');
        (isTestEmailSend ? setSendingTest : setSaving)(false);
        return;
      }
    }
  };

  useEffect(() => {
    if (!open) return;

    if (passcodeList) {
      setWorkingPasscodeList(mapToWorkingPasscodeList(projectUuid, passcodeList, isMagicLinkRegistrations));
    } else {
      setWorkingPasscodeList(getDefaultPasscodeList(projectUuid ? projectUuid : '', isMagicLinkRegistrations));
    }
  }, [passcodeList, isMagicLinkRegistrations, projectUuid, open]);

  function displayCaughtError(e: unknown, defaultMessage: string) {
    toastTrigger(getFetchErrorMessage(e, defaultMessage), {
      type: EToastTypes.error,
      autoClose: 10000,
    });
  }

  const saveDisabled = useCallback(() => {
    return saving || !allListMembersValid(workingPasscodeList);
  }, [saving, workingPasscodeList]);

  const getPasscodeListId = () => {
    let passcodeListId = 0;
    if (isMagicLinkRegistrations && magicLinkRegistrationsPasscodeListId) {
      passcodeListId = magicLinkRegistrationsPasscodeListId;
    } else if (passcodeList?.passcode_list) {
      passcodeListId = passcodeList.passcode_list;
    }

    return passcodeListId;
  };

  const handleResetModal = () => {
    const passcodeListId = getPasscodeListId();
    closeAndResetState(passcodeListId);
  };

  return (
    <Modal
      open={open}
      onRequestClose={sendTestEmailModalOpen ? undefined : handleResetModal}
      customSize={{
        width: '960px',
        height: '800px',
        minHeight: '400px',
        maxHeight: 'calc(100% - 32px)',
        maxWidth: 'calc(100% - 32px)',
      }}
      withZoom={false}
      backgroundColor="var(--gray-900)"
      RenderHeaderContent={() => {
        return (
          <>
            <OptionalComponent display={!isMagicLinkRegistrations}>
              <Container padding="20px 40px" borderRadius="16px">
                <Text className="H3 padding-b-16">Add audience members to {workingPasscodeList?.name}</Text>
                <Text className="H1">Add profiles to your target audience seamlessly.</Text>
              </Container>
            </OptionalComponent>
            <OptionalComponent display={!!isMagicLinkRegistrations}>
              <Container padding="20px 40px" borderRadius="16px">
                <Text className="H3 padding-b-16">Add registrants</Text>
                <Text className="H2">Register your target audience seamlessly.</Text>
              </Container>
            </OptionalComponent>
          </>
        );
      }}
      RenderFooterContent={() => (
        <>
          <OptionalComponent display={workingPasscodeList.type === PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION}>
            <Text
              color="var(--semantic-red-500)"
              fontSize="11px"
              className="flex fd-row jc-flex-start padding-h-40 padding-v-16"
            >
              PLEASE NOTE THAT THIS ACTION WILL AUTOMATICALLY SEND ALL ADDED OR UPLOADED USERS A REGISTRATION
              CONFIRMATION EMAIL IMMEDIATELY UPON SAVING.
            </Text>
          </OptionalComponent>
          <Container
            background="var(--gray-900)"
            className="flex fd-row jc-flex-end ai-flex-end gap-8"
            padding="12px 40px"
            borderRadius="16px"
          >
            <Button onClick={handleResetModal} design="LocalBttnSec" className="L1 padding-v-16 padding-h-24">
              Cancel
            </Button>
            <Button
              width="auto"
              padding="16px 24px"
              loading={saving}
              disabled={saveDisabled()}
              onClick={() => {
                if (isMagicLinkRegistrations) {
                  setSendTestEmailModalOpen(true);
                } else {
                  handleSaving(false);
                }
              }}
              backgroundColor={getThemeColorCSSVariable()}
              hoverBackgroundColor={getThemeColorsCssMixVariable()}
            >
              <Text
                color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
                className="L1"
              >
                Save
              </Text>
            </Button>
          </Container>
        </>
      )}
    >
      <AddPasscodeListMembers
        setWorkingPasscodeList={setWorkingPasscodeList}
        workingPasscodeList={workingPasscodeList}
        bubbleFileName={setFileName}
      />

      <ConfirmUploadRegistrantsModal
        open={sendTestEmailModalOpen}
        setOpen={setSendTestEmailModalOpen}
        setTestEmailAddressesForSend={setTestEmailAddressesForSend}
        sendEmails={handleSaving}
        isSaving={saving}
        isSendingTest={sendingTest}
        isBypassPasscodeListUploadForTestEmail
      />
    </Modal>
  );
};

export default AddPasscodeListMembersModal;
